<template>
  <div class="w-100 p-2">
    <page-header>
      <i class="fas fa-list"></i> {{ $t('page.title.players_kyc') }}
    </page-header>
    <page-card>
      <template v-slot:header>
        {{ $t('page.filters')}}
      </template>
      <template v-slot:content>
        <div class="sygni-grid">
            <div class="row">
              <div class="col-6">
                <filter-input
                    :filter-query="filterQuery"
                    @store="storeFilter"
                    :store-data="filters"
                    filter-name="phrase"
                    :label="$t('filters.label.player_phrase')"
                    :placeholder="$t('filters.placeholder.player_phrase')">
                </filter-input>
              </div>


              <div class="col-6">
                <filter-select
                    :filter-query="filterQuery"
                    :options="filterOptions.status"
                    :default-filter="filterOptions.status[3]"
                    :multiple="false"
                    options-label="text"
                    options-track-by="value"
                    filter-name="kycStatus"
                    :label="$t('filters.label.kyc_status')"
                    :placeholder="$t('filters.placeholder.kyc_status')">
                </filter-select>
              </div>
            </div> <!-- #row -->

            <div class="row">
              <div class="col-6">
                <filter-input
                    :filter-query="filterQuery"
                    @store="storeFilter"
                    :store-data="filters"
                    filter-name="phone"
                    :label="$t('filters.label.phone')"
                    :placeholder="$t('filters.placeholder.phone')">
                </filter-input>
              </div>
              <div class="col-6">
                <filter-date-range-picker
                    :filter-query="filterQuery"
                    :locale="{firstDay: 1, format: 'yyyy-mm-dd'}"
                    @store="storeFilter"
                    :store-data="filters"
                    :auto="true"
                    :linked="false"
                    :time-picker="true"
                    filter-name="kycUpdatedAt"
                    open-direction="left"
                    :label="$t('filters.label.kyc_updated_at')">
                </filter-date-range-picker>
              </div>
            </div> <!-- #row -->

            <div class="row">
              <div class="col-6">
                <filter-select
                    :filter-query="filterQuery"
                    @store="storeFilter"
                    :store-data="filters"
                    :options="filterOptions.country"
                    :multiple="false"
                    options-track-by="value"
                    options-label="text"
                    filter-name="country"
                    :label="$t('filters.label.country')"
                    :placeholder="$t('filters.placeholder.country')">
                </filter-select>
              </div>
              <div class="col-6">
                <filter-date-range-picker
                    :filter-query="filterQuery"
                    :locale="{firstDay: 1, format: 'yyyy-mm-dd'}"
                    @store="storeFilter"
                    :store-data="filters"
                    :auto="true"
                    :time-picker="true"
                    :linked="false"
                    filter-name="lastLogin"
                    open-direction="left"
                    :label="$t('filters.label.last_login_at')">
                </filter-date-range-picker>
              </div>
            </div> <!-- #row -->
        </div>
      </template>
      <template v-slot:footer>
        <b-button @click="resetFilters()" variant="secondary">{{$t('filters.btn.clear')}}</b-button>
        <b-button @click="applyFilters()" variant="primary">{{$t('filters.btn.apply')}}</b-button>
      </template>
    </page-card>

    <page-card>
      <template v-slot:header>
        {{ $t('page.list')}}
      </template>
      <template v-slot:content>
        <div class="sygni-grid">

          <div class="row mt-2">
            <div class="col-12">
              <grid-pagination :total-count="totalCount"
                               :per-page="perPage"
                               :current-page="currentPage"
                               @load-items="paginationReload"></grid-pagination>

              <div class="table-responsive">
                <b-table striped show-empty
                         :empty-text="$t('table.no_results')"
                         :busy="isBusy"
                         hover
                         head-variant="dark"
                         outlined
                         :sort-by.sync="orderColumn"
                         :sort-desc.sync="orderDirection"
                         :no-local-sorting="true"
                         :items="items"
                         :fields="fields">

                  <template #cell(kycStatus)="data">
                    <b-badge pill variant="warning" v-if="data.value === 'pending'">{{ $t('kyc.label.status.'+data.value) }}</b-badge>
                    <b-badge pill variant="danger" v-if="data.value === 'rejected'">{{ $t('kyc.label.status.'+data.value) }}</b-badge>
                    <b-badge pill variant="secondary" v-if="data.value === 'unverified'">{{ $t('kyc.label.status.'+data.value) }}</b-badge>
                    <b-badge pill variant="success" v-if="data.value === 'approved'">{{ $t('kyc.label.status.'+data.value) }}</b-badge>
                    <b-badge pill variant="info" v-if="data.value === 'requested'">{{ $t('kyc.label.status.'+data.value) }}</b-badge>
                  </template>

                  <template #cell(createdAt)="data">
                    {{ data.value | dateFormat('YYYY-MM-DD HH:mm') }}
                  </template>

                  <template #cell(kycUpdatedAt)="data">
                    {{ data.value | dateFormat('YYYY-MM-DD HH:mm') }}
                  </template>

                  <template #cell(lastLogin)="data">
                    {{ data.value | dateFormat('YYYY-MM-DD HH:mm') }}
                  </template>

                  <template #cell(country)="data">
                    <div class="table-flag--wrapper" v-if="data.value">
                  <span>

                  <img
                      v-if="require(`@/assets/flags/${data.value.toLowerCase()}.svg`)"
                      :src="require(`@/assets/flags/${data.value.toLowerCase()}.svg`)"
                      class="table-flags"
                  >
                  {{ data.value.toUpperCase() }}
                  </span>
                    </div>
                  </template>

                  <template #cell(quizCoinAmount)="data">
                    {{ data.value | numberFormat }}
                  </template>

                  <template #cell(freeCoinAmount)="data">
                    {{ data.value | numberFormat }}
                  </template>

                  <template #cell(actions)="data">
                    <div class="table-action--cell">
                      <b-button variant="primary" size="sm"
                                @click="showItemDetails(data.item)">
                        {{ $t('table.btn.show') }}
                      </b-button>
                    </div>
                  </template>

                  <template #table-busy>
                    <div class="text-center text-danger my-2">
                      <b-spinner class="align-middle"></b-spinner>
                      <strong>{{$t('state.loading')}}</strong>
                    </div>
                  </template>

                </b-table>
              </div> <!-- #table-responsive-->

              <grid-pagination :total-count="totalCount"
                               :per-page="perPage"
                               :current-page="currentPage"
                               @load-items="paginationReload"></grid-pagination>

            </div>
          </div>
        </div>
      </template>
    </page-card>
  </div>
</template>

<script>

import FilterSelect from "../../../components/grid/filters/FilterSelect";
import FilterInput from "../../../components/grid/filters/FilterInput";
import FilterDateRangePicker from "../../../components/grid/filters/FilterDateRangePicker";
import GridPagination from "../../../components/grid/grid-pagination/GridPagination";
import PageHeader from "../../../components/page/PageHeader";
import PageCard from "../../../components/page/PageCard";

export default {
  props: ['apiKey'],
  components: {
    PageCard,
    PageHeader,
    GridPagination,
    FilterSelect,
    FilterInput,
    FilterDateRangePicker,
  },
  data() {
    return {
      perPage: 10,
      currentPage: 1,
      orderColumn: 'createdAt',
      orderDirection: false,
      fields: [
        // {key: 'id'},
        {key: 'name', label: this.$t('table.header.first_name_last_name')},
        {key: 'nickName', label: this.$t('table.header.nick_name')},
        {key: 'email', label: this.$t('table.header.email')},
        {key: 'phone', label: this.$t('table.header.phone')},
        {key: 'country', label: this.$t('table.header.country'), sortable: true},
        {key: 'kycStatus', label: this.$t('table.header.kyc_status'), sortable: true},
        {key: 'kycUpdatedAt', label: this.$t('table.header.kyc_updated_at'), sortable: true},
        {key: 'createdAt', label: this.$t('table.header.registred_at'), sortable: true},
        {key: 'lastLogin', label: this.$t('table.header.last_login_at'), sortable: true},
        {key: 'actions', label: this.$t('table.header.actions')},
      ],
      selectOptions: {
        status: [
          {value: 20, text: 'published'},
          {value: 10, text: 'draft'},
        ],
        type: [
          {value: 'text', text: 'text'},
          {value: 'image', text: 'image'},
          {value: 'sound', text: 'sound'},
          {value: 'video', text: 'video'},
        ],
        containOperator: [
          {value: 'eq', text: this.$t('operator.contains')},
          {value: 'neq', text: this.$t('operator.not_contains')},
        ],
        comparisonOperator: [
          {value: 'eq', text: this.$t('operator.equales')},
          {value: 'gte', text: this.$t('operator.greater_then')},
          {value: 'lte', text: this.$t('operator.lower_than')},
          {value: 'btw', text: this.$t('operator.between')},
        ],
        bulkActions: [
          {value: null, text: 'Bulk actions'},
          {value: 'export', text: 'Export'},
          {value: 'delete', text: 'Delete'},
        ],
        difficulty: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10],
        lang: [],
        category: [],
      },
      filterQuery: [],
      filterOptions: {
        lobbyId: [
          {value: 'INSTANT', text: 'Instant'},
          {value: 'SIT_AND_GO', text: 'Sit & Go'}
        ],
        country: [
          {value: 'PL', text: 'Poland'},
          {value: 'DE', text: 'Germany'},
          {value: 'FR', text: 'France'},
        ],
        status: [
          {value: 'requested', text: this.$t('kyc.label.status.requested')},
          {value: 'approved', text: this.$t('kyc.label.status.approved')},
          {value: 'unverified', text: this.$t('kyc.label.status.unverified')},
          {value: 'pending', text: this.$t('kyc.label.status.pending')},
          {value: 'rejected', text: this.$t('kyc.label.status.rejected')}
        ],
        walletCurrency: [
          {value: 'FUN_COINS', text: 'Free Coins'},
          {value: 'QUIZ_COINS', text: 'Quiz Coins'}
        ],
        maxPlayers: [
          {value: '2', text: '2'},
          {value: '3', text: '3'},
          {value: '4', text: '4'}
        ],
        comparisonOperator: [
          {value: 'eq', text: this.$t('operator.equales')},
          {value: 'gte', text: this.$t('operator.greater_then')},
          {value: 'lte', text: this.$t('operator.lower_than')},
          {value: 'btw', text: this.$t('operator.between')},
        ],
        category: [],
      },
    };
  },
  mounted() {
    if (this.apiKey) {
      this.$store.commit('root/setApiKey', this.apiKey);
    }
    this.loadItems();
    if(!this.filters) {
      this.initFilters();
    }
    document.addEventListener('keydown', this.bindEnter);
  },
  beforeDestroy() {
    document.removeEventListener('keydown', this.bindEnter);
  },
  computed: {
    filters: function () {
      return this.$store.getters["root/filter"]('players-kyc');
    },
    isBusy: function () {
      return this.$store.getters["players/isBusy"];
    },
    items: function () {
      return this.$store.getters["players/items"];
    },
    totalCount: function () {
      return this.$store.getters["players/totalCount"];
    },
  },
  methods: {
    storeFilter(data) {
      this.$store.commit('root/setFilter', {storeName: 'players-kyc', ...data})
    },
    createSortQuery() {
      let sorting;
      if (this.orderColumn) {
        if (this.orderDirection === true) {
          sorting = encodeURIComponent('+' + this.orderColumn);
        } else {
          sorting = encodeURIComponent('-' + this.orderColumn);
        }
        return '&sort[]=' + sorting;
      }
      return '';
    },
    bindEnter(e) {
      if (e.keyCode === 13) {
        this.applyFilters();
      }
    },
    loadItems() {
      this.$store.dispatch('players/loadItems', {
        params: {
          offset: this.perPage * (this.currentPage - 1),
          limit: this.perPage
        },
        filters: this.filterQuery,
        sort: this.createSortQuery()
      });
    },
    applyFilters: function () {
      this.currentPage = 1;
      this.loadItems();
    },
    resetFilters() {
      this.filterQuery = [];
      this.$root.$emit('resetFilters');
      this.initFilters();
      this.applyFilters();
    },
    initFilters() {
      this.$store.commit('root/initFilters', {
        storeName: 'players-kyc',
        filterNames: ['phrase', 'kycStatus', 'phone', 'lastLogin', 'kycUpdatedAt', 'country']
      });
    },
    showItemDetails(item) {
      this.$router.push({name: 'player-kyc', params: {id: item.id}});
    },
    paginationReload(data) {
      this.currentPage = data.currentPage;
      this.perPage = data.perPage;
      // this.totalCount = data.totalCount;
      this.loadItems();
    }
  },
  watch: {
    orderColumn: function () {
      this.loadItems();
    },
    orderDirection: function () {
      this.loadItems();
    }
  }
}
</script>
